import { useMemo, useState } from "react";
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth, DEPARTMENT_LABELS } from "@/lib/auth-context";
import { type ModeratorPermissions } from "@/lib/moderator-permissions";
import {
  listNotificationHistory,
  deletePushNotification,
} from "@/lib/rpc/notifications.functions";
import { formatIst } from "@/lib/tz";

export const Route = createFileRoute("/_app/notifications/history")({
  component: HistoryPage,
});

function HistoryPage() {
  const { profile, role, loading } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const canSee = useMemo(() => {
    if (!profile) return false;
    if (role === "admin") return true;
    if (profile.department === "hr") return true;
    if (role === "moderator") {
      const perms = (profile.moderator_permissions ?? {}) as ModeratorPermissions;
      return Boolean(perms.send_notifications);
    }
    return false;
  }, [profile, role]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["notification-history"],
    enabled: canSee,
    queryFn: () => listNotificationHistory(),
  });

  const del = useMutation({
    mutationFn: (id: string) => deletePushNotification({ data: { id } }),
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["notification-history"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (loading) return null;
  if (!canSee) return <Navigate to="/dashboard" />;

  const filtered = rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !r.heading.toLowerCase().includes(q) &&
        !r.description.toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  return (
    <div className="flex flex-col">
      <AppHeader subtitle="All notifications you've sent" />
      <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-[18px] font-bold leading-tight">Notification History</h2>
            <p className="mt-1 text-[12px] text-muted-foreground">
              {role === "admin" ? "All notifications across the workspace" : "Your sent notifications"}
            </p>
          </div>
          <Button asChild size="sm">
            <Link to="/notifications/send">
              <Plus className="mr-1 h-4 w-4" /> Send New
            </Link>
          </Button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search heading or description"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 max-w-xs"
          />
          <div className="flex gap-1">
            {(["all", "sent", "pending", "draft"] as const).map((s) => (
              <Button
                key={s}
                size="sm"
                variant={statusFilter === s ? "default" : "outline"}
                className="h-8 capitalize"
                onClick={() => setStatusFilter(s)}
              >
                {s}
              </Button>
            ))}
          </div>
        </div>

        <div className="mt-4 rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Heading</TableHead>
                <TableHead>Audience</TableHead>
                <TableHead>Sent / Scheduled</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Read Rate</TableHead>
                {role === "admin" && <TableHead>Sender</TableHead>}
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={role === "admin" ? 7 : 6} className="text-center text-xs text-muted-foreground py-10">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={role === "admin" ? 7 : 6} className="text-center text-xs text-muted-foreground py-10">
                    No notifications
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => {
                  const audience =
                    r.target_type === "all"
                      ? "All employees"
                      : r.target_type === "department"
                        ? (r.target_dept ?? [])
                            .map((d) => DEPARTMENT_LABELS[d as keyof typeof DEPARTMENT_LABELS] ?? d)
                            .join(", ")
                        : r.target_user_name ?? "Individual";
                  const when = r.sent_at
                    ? formatIst(r.sent_at, "MMM d, yyyy HH:mm")
                    : r.scheduled_at
                      ? `→ ${formatIst(r.scheduled_at, "MMM d, yyyy HH:mm")}`
                      : formatIst(r.created_at, "MMM d, yyyy HH:mm");
                  const readPct =
                    r.audience_total > 0
                      ? Math.round((r.read_count / r.audience_total) * 100)
                      : 0;
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="max-w-xs">
                        <div className="font-medium text-sm truncate">{r.heading}</div>
                        <div className="text-xs text-muted-foreground line-clamp-1">
                          {r.description}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{audience}</TableCell>
                      <TableCell className="text-xs">{when}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            r.status === "sent"
                              ? "default"
                              : r.status === "pending"
                                ? "secondary"
                                : "outline"
                          }
                          className="capitalize"
                        >
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-xs">
                        {r.status === "sent" ? `${r.read_count}/${r.audience_total} (${readPct}%)` : "—"}
                      </TableCell>
                      {role === "admin" && (
                        <TableCell className="text-xs">{r.created_by_name ?? "—"}</TableCell>
                      )}
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete notification?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This removes "{r.heading}" from history. This cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del.mutate(r.id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
